import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthService } from './auth.service';

/**
 * Progress update event from SignalR
 */
export interface ProgressUpdate {
  percentage: number;
  currentSheet: string;
  message?: string;
}

/**
 * Validation completed event from SignalR
 */
export interface ValidationCompleted {
  sessionId: string;
  success: boolean;
  message?: string;
}

/**
 * Validation failed event from SignalR
 */
export interface ValidationFailed {
  sessionId: string;
  errorMessage: string;
}

/**
 * SignalR service for real-time validation updates
 */
@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private authService = inject(AuthService);
  
  private hubConnection: HubConnection | null = null;
  private connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private _connectingPromise: Promise<void> | null = null;

  // Observables for validation events
  private progressUpdate$ = new Subject<ProgressUpdate>();
  private validationCompleted$ = new Subject<ValidationCompleted>();
  private validationFailed$ = new Subject<ValidationFailed>();

  /**
   * Get connection state as observable
   */
  get connectionState(): Observable<HubConnectionState> {
    return this.connectionState$.asObservable();
  }

  /**
   * Get progress update observable
   */
  get onProgressUpdate(): Observable<ProgressUpdate> {
    return this.progressUpdate$.asObservable();
  }

  /**
   * Get validation completed observable
   */
  get onValidationCompleted(): Observable<ValidationCompleted> {
    return this.validationCompleted$.asObservable();
  }

  /**
   * Get validation failed observable
   */
  get onValidationFailed(): Observable<ValidationFailed> {
    return this.validationFailed$.asObservable();
  }

  /**
   * Check if connection is established
   */
  get isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  /**
   * Establish SignalR connection to validation hub
   */
  async connect(): Promise<void> {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      console.log('SignalR already connected');
      return;
    }
    if (this._connectingPromise) {
      return this._connectingPromise;
    }
    this._connectingPromise = this._doConnect().finally(() => {
      this._connectingPromise = null;
    });
    return this._connectingPromise;
  }

  /**
   * Internal connection implementation
   */
  private async _doConnect(): Promise<void> {
    try {
      // Get access token for authentication
      const token = await this.authService.getAccessToken();

      // Build hub connection
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${environment.apiUrl}/hubs/validation`, {
          accessTokenFactory: () => token || ''
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0s, 2s, 10s, 30s, then 60s
            if (retryContext.previousRetryCount === 0) return 0;
            if (retryContext.previousRetryCount === 1) return 2000;
            if (retryContext.previousRetryCount === 2) return 10000;
            if (retryContext.previousRetryCount === 3) return 30000;
            return 60000;
          }
        })
        .build();

      // Register event handlers
      this.registerEventHandlers();

      // Start connection
      await this.hubConnection.start();
      this.connectionState$.next(HubConnectionState.Connected);
      console.log('SignalR connected successfully');
    } catch (error) {
      console.error('SignalR connection failed:', error);
      this.connectionState$.next(HubConnectionState.Disconnected);
      throw error;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
        this.connectionState$.next(HubConnectionState.Disconnected);
        console.log('SignalR disconnected');
      } catch (error) {
        console.error('SignalR disconnect error:', error);
      } finally {
        this.hubConnection = null;
      }
    }
  }

  /**
   * Subscribe to validation progress updates for a specific session
   */
  async subscribeToValidation(sessionId: string): Promise<void> {
    if (!this.isConnected) {
      await this.connect();
    }

    try {
      await this.hubConnection!.invoke('SubscribeToValidation', sessionId);
      console.log(`Subscribed to validation session: ${sessionId}`);
    } catch (error) {
      console.error('Failed to subscribe to validation:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from validation updates for a specific session
   */
  async unsubscribeFromValidation(sessionId: string): Promise<void> {
    if (!this.isConnected) return;

    try {
      await this.hubConnection!.invoke('UnsubscribeFromValidation', sessionId);
      console.log(`Unsubscribed from validation session: ${sessionId}`);
    } catch (error) {
      console.error('Failed to unsubscribe from validation:', error);
    }
  }

  /**
   * Register SignalR event handlers
   */
  private registerEventHandlers(): void {
    if (!this.hubConnection) return;

    // Progress update event
    this.hubConnection.on('ProgressUpdate', (data: ProgressUpdate) => {
      console.log('Progress update:', data);
      this.progressUpdate$.next(data);
    });

    // Validation completed event
    this.hubConnection.on('ValidationCompleted', (data: ValidationCompleted) => {
      console.log('Validation completed:', data);
      this.validationCompleted$.next(data);
    });

    // Validation failed event
    this.hubConnection.on('ValidationFailed', (data: ValidationFailed) => {
      console.log('Validation failed:', data);
      this.validationFailed$.next(data);
    });

    // Connection lifecycle events
    this.hubConnection.onreconnecting((error) => {
      console.warn('SignalR reconnecting...', error);
      this.connectionState$.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('SignalR reconnected:', connectionId);
      this.connectionState$.next(HubConnectionState.Connected);
    });

    this.hubConnection.onclose((error) => {
      console.error('SignalR connection closed:', error);
      this.connectionState$.next(HubConnectionState.Disconnected);
    });
  }
}
